// The gun rack's readout: what the visor shows while the camera is on the rack. Everything sits in one
// column down the left so the rack itself keeps the rest of the frame:
//
//   the ladder   four rows, top to bottom like the rack: class badges, sidearms, primaries, grenades.
//                Each row shows what's equipped; the row you're on opens into ◀ item ▶ with a pip per
//                item. ↑↓ (D-pad or left stick) picks the row, ←→ browses it, and the camera and the
//                brackets on the rack follow.
//   the card     the browsed item read out in full: a class's dance with its buttons, a gun's stats.
//   the hints    ✕ equips, ○ goes back to the bar.
//
// Equipping is immediate: the class goes to the server (between waves only, and the saloon is
// always between waves) and the loadout is saved per class and sent, like the old loadout panel.

import { CLASSES, ClassId } from '../../sim/defs';
import { Fire, GRENADES, Loadout, PRIMARIES, SECONDARIES, WEAPONS, type WeaponDef, type GrenadeDef } from '../../sim/weapons';
import { TICK_RATE } from '../../sim/core';
import { psGlyph } from '../input/padnav';
import { emblemSvg } from './emblems';
import { CLASS_INFO } from './classinfo';
import { PadEdges, VISOR_CSS, esc } from './ui';

export type RackKind = 'cls' | 'gun' | 'nade';

const AP_NAME = ['AP1', 'AP2', 'AP3', 'AP4', 'AP5', 'AP5']; // AP.Ordnance is 5 (the gap above anti-tank is deliberate, defs.ts)
const NADE_HEX = ['#d8b060', '#ff7a2a', '#7fd6ff', '#f3e65a', '#9be03a', '#b07aff', '#9a8e80', '#f4f1e8', '#86b04a', '#5ad1c8'];

const CSS = VISOR_CSS + `
.rk { position: fixed; inset: 0; z-index: 9; pointer-events: none; opacity: 0; transition: opacity .3s; }
.rk.on { opacity: 1; }
.rk-panel { position: absolute; left: max(3vw, 16px); top: max(3.5vh, 14px); max-height: calc(100vh - 2 * max(3.5vh, 14px)); width: min(460px, calc(100vw - 32px));
  display: flex; flex-direction: column; gap: clamp(8px, 1.6vh, 14px); padding: clamp(12px, 2vh, 18px) 26px clamp(12px, 2vh, 18px) 20px; pointer-events: auto; }
.rk-panel::before { content: ''; position: absolute; inset: 0; z-index: -1;
  background: linear-gradient(180deg, rgba(6,16,16,.36), rgba(6,16,16,.24) 60%, rgba(6,16,16,.32)); backdrop-filter: blur(6px);
  clip-path: polygon(16px 0, 100% 0, 100% calc(100% - 16px), calc(100% - 16px) 100%, 0 100%, 0 16px); }
.rk-title { display: flex; justify-content: space-between; align-items: baseline; font-size: 11px; font-weight: 600; letter-spacing: 3.5px; text-transform: uppercase; color: rgba(var(--ph), .5); }

/* the ladder */
.rk-ladder { display: flex; flex-direction: column; }
.rk-step { position: relative; display: grid; grid-template-columns: 82px 1fr; align-items: center; min-height: clamp(26px, 4.2vh, 32px); padding: 0 8px; cursor: pointer; }
.rk-step .lab { font-size: 12px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: rgba(var(--ph), .5); }
.rk-step .val { display: flex; align-items: center; gap: 8px; min-width: 0; font-size: clamp(14px, 2.1vh, 16px); font-weight: 500; color: rgba(var(--ph), .72); }
.rk-step .val .nm { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rk-step .emb { width: 18px; height: 18px; flex: 0 0 auto; }
.rk-step .dia { width: 8px; height: 8px; transform: rotate(45deg); flex: 0 0 auto; }
.rk-step.on { background: linear-gradient(90deg, rgba(var(--ph), .1), rgba(var(--ph), .03)); }
.rk-step.on::before { content: ''; position: absolute; left: 0; top: 4px; bottom: 4px; width: 2px; background: rgba(var(--amb), .95); box-shadow: 0 0 8px rgba(var(--amb), .6); }
.rk-step.on .lab { color: rgba(var(--amb), 1); }
.rk-step.on .val { color: rgba(var(--ph), 1); }
.rk-step .arr { flex: 0 0 auto; width: 22px; height: 22px; display: grid; place-items: center; font-size: 11px; color: rgba(var(--ph), .7); }
.rk-step .arr:hover { color: rgba(var(--amb), 1); }
.rk-step .grow { flex: 1 1 auto; min-width: 0; display: flex; align-items: center; gap: 8px; }
.rk-step .eqd.off { color: rgba(var(--ph), .7); }
.rk-step .eqd .psg { width: 1.3em; height: 1.3em; margin-right: 4px; }
.rk-step .eqd .cap-k { margin-right: 4px; }
.rk-step .eqd { display: inline-flex; align-items: center; flex: 0 0 auto; font-size: 10px; font-weight: 600; letter-spacing: 2px; color: rgba(var(--amb), .95); }
.rk-pips { grid-column: 2; display: flex; gap: 5px; padding: 1px 0 7px 30px; }
.rk-pips i { width: 14px; height: 4px; background: rgba(var(--ph), .32); }
.rk-pips i.eq { background: rgba(var(--amb), .8); }
.rk-pips i.cur { background: rgba(var(--ph), 1); box-shadow: 0 0 5px rgba(var(--ph), .7); }

/* the card */
.rk-card { flex: 0 1 auto; min-height: 0; overflow: hidden; display: flex; flex-direction: column; border-top: 1px solid rgba(var(--ph), .14); padding-top: clamp(8px, 1.6vh, 14px); }
.rk-name { font-size: clamp(22px, 3.7vh, 30px); font-weight: 600; line-height: 1.05; display: flex; align-items: center; gap: 12px; }
.rk-name .emb { flex: 0 0 auto; width: 1.3em; height: 1.3em; }
.rk-role { margin-left: auto; align-self: flex-end; font-size: 11px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(var(--ph), .55); padding-bottom: 3px; }
.rk-sub { font-size: 11px; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(var(--ph), .55); margin-top: 4px; }
.rk-chips { display: flex; gap: 6px; margin-top: 8px; flex-wrap: wrap; }
.rk-chip { font-size: 11px; font-weight: 600; letter-spacing: 1.5px; padding: 3px 8px; background: rgba(var(--ph), .12); text-transform: uppercase; }
.rk-chip.on { background: rgba(var(--amb), .2); color: rgba(var(--amb), 1); }
.rk-blurb { font-size: clamp(13px, 2vh, 15px); line-height: 1.4; margin-top: clamp(6px, 1.2vh, 10px); color: rgba(var(--ph), .82); }
.rk-bio { font-style: italic; color: rgba(var(--ph), .7); }
.rk-loop { margin-top: clamp(6px, 1.2vh, 10px); font-size: clamp(12.5px, 1.9vh, 14px); font-weight: 600; color: rgba(var(--ph), .92); }
.rk-dance { margin-top: clamp(6px, 1.2vh, 10px); font-size: clamp(12.5px, 1.9vh, 14px); line-height: 1.38; }
.rk-dance > div { margin-top: clamp(4px, .8vh, 7px); color: rgba(var(--ph), .88); }
.rk-dance .k { font-size: 11.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: rgba(var(--amb), .95); margin-right: 8px; white-space: nowrap; }
.rk-watch { margin-top: clamp(8px, 1.4vh, 12px); font-size: clamp(12px, 1.8vh, 13.5px); line-height: 1.35; color: rgba(var(--ph), .62); }
.rk-btn { display: inline-block; padding: 0 5px; margin: 0 1px; font-size: .78em; font-weight: 700; letter-spacing: .5px; line-height: 1.45; vertical-align: .08em;
  border: 1px solid rgba(var(--ph), .55); border-radius: 3px; color: rgba(var(--ph), 1); }
.rk-card .psg { width: 1.2em; height: 1.2em; vertical-align: -0.28em; margin: 0 1px; }
.rk-stats { margin-top: clamp(8px, 1.6vh, 12px); display: grid; grid-template-columns: 84px 1fr 74px; gap: clamp(4px, .9vh, 7px) 10px; align-items: center; font-size: 11.5px; letter-spacing: 2px; }
.rk-stats.two { grid-template-columns: 74px 1fr 34px 74px 1fr 34px; }
.rk-stats .l { color: rgba(var(--ph), .55); }
.rk-stats .v { font-size: 12.5px; letter-spacing: .5px; font-variant-numeric: tabular-nums; text-align: right; white-space: nowrap; }
.rk-stats i { display: block; height: 4px; background: rgba(var(--ph), .12); position: relative; }
.rk-stats i b { position: absolute; inset: 0 auto 0 0; background: rgba(var(--ph), .8); box-shadow: 0 0 6px rgba(var(--ph), .5); }

/* the hints */
.rk-hints { display: flex; flex-wrap: wrap; gap: 6px 14px; font-size: 12px; font-weight: 500; letter-spacing: 1.4px; text-transform: uppercase; color: rgba(var(--ph), .8);
  border-top: 1px solid rgba(var(--ph), .14); padding-top: clamp(8px, 1.4vh, 12px); }
.rk-hints > span { display: inline-flex; align-items: center; white-space: nowrap; }
.rk-hints .ar { font-size: .85em; letter-spacing: 1px; margin-right: 7px; color: rgba(var(--ph), 1); }
.rk-hints .eqh { opacity: .45; }
.rk-hints .eqh.hot { opacity: 1; color: rgba(var(--amb), 1); cursor: pointer; }
.rk-card .dp path { fill: rgba(var(--ph), .45); }
.rk-card .dp path.hot { fill: rgba(var(--ph), .95); }
.rk-card .dp { width: 1.5em; height: 1.5em; vertical-align: -0.32em; margin: 0 2px; }

/* the brackets on the rack item */
.rk-br { position: absolute; width: 16px; height: 16px; border-color: rgba(var(--amb), .9); border-style: solid; border-width: 0; transition: left .18s, top .18s, opacity .2s; filter: drop-shadow(0 0 4px rgba(var(--amb), .6)); }
.rk-br.a { border-left-width: 2px; border-top-width: 2px; } .rk-br.b { border-right-width: 2px; border-top-width: 2px; }
.rk-br.c { border-left-width: 2px; border-bottom-width: 2px; } .rk-br.d { border-right-width: 2px; border-bottom-width: 2px; }
.rk-tag { position: absolute; font-size: 11px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: rgba(var(--amb), 1); transition: left .18s, top .18s; white-space: nowrap; }

/* big screens: the same column, scaled up so it reads the same size */
@media (min-width: 1600px) and (min-height: 950px) { .rk-panel { zoom: 1.2; } }
@media (min-width: 2400px) and (min-height: 1300px) { .rk-panel { zoom: 1.5; } }
@media (max-height: 640px) { .rk-watch { display: none; } .rk-role { margin-left: auto; align-self: flex-end; font-size: 11px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(var(--ph), .55); padding-bottom: 3px; }
.rk-sub { display: none; } }
@media (max-width: 720px) { .rk-panel { left: 8px; right: 8px; width: auto; top: auto; max-height: 64vh; } .rk-step { grid-template-columns: 70px 1fr; } }
`;

interface RowDef { label: string; kind: RackKind; ids: number[] }

export interface RackHooks {
  /** the browsed item changed: point the camera at it */
  browse(kind: RackKind, id: number): void;
  equipClass(c: ClassId): void;
  equipLoadout(l: Loadout): void;
  close(changed: boolean): void;
  sound(name: 'ui_move' | 'ui_select'): void;
}

const tick = 1 / TICK_RATE;
/** shots a second, counting bursts and charge-up */
function shotsPerSec(w: WeaponDef) {
  if (w.fire === Fire.Burst) return w.burst / ((w.interval + w.burstGap * (w.burst - 1)) * tick);
  if (w.fire === Fire.Charge) return 1 / ((w.charge + w.interval) * tick);
  return 1 / (w.interval * tick);
}
const FIRE_NAME = ['SEMI', 'AUTO', 'BURST', 'CHARGE'];

/** a d-pad with the arms that do something lit: 'ud' rows, 'lr' items */
function dpad(arms: 'ud' | 'lr' | 'd') {
  const u = 'M7.5 1h5v6l-2.5 2.5L7.5 7z', d = 'M7.5 19h5v-6l-2.5-2.5L7.5 13z';
  const l = 'M1 7.5v5h6l2.5-2.5L7 7.5z', r = 'M19 7.5v5h-6l-2.5-2.5L13 7.5z';
  const hot = (on: boolean) => (on ? ' class="hot"' : '');
  return `<svg class="dp" viewBox="0 0 20 20" aria-hidden="true"><path d="${u}"${hot(arms === 'ud')}/><path d="${d}"${hot(arms === 'ud' || arms === 'd')}/><path d="${l}"${hot(arms === 'lr')}/><path d="${r}"${hot(arms === 'lr')}/></svg>`;
}

export class RackUI {
  private root = document.createElement('div');
  private panel = document.createElement('div');
  private ladder = document.createElement('div');
  private card = document.createElement('div');
  private hints = document.createElement('div');
  private br: HTMLElement[] = [];
  private tag = document.createElement('div');
  private rows: RowDef[] = [
    { label: 'Class', kind: 'cls', ids: [0, 1, 2, 3] },
    { label: 'Sidearm', kind: 'gun', ids: SECONDARIES },
    { label: 'Primary', kind: 'gun', ids: PRIMARIES },
    { label: 'Grenade', kind: 'nade', ids: GRENADES.map((g) => g.id) },
  ];
  private row = 0;
  private cur = [0, 0, 0, 0];
  private cls: ClassId = 0;
  private lo!: Loadout;
  private changed = false;
  private hooks: RackHooks | null = null;
  private pad = new PadEdges();
  glyphs: 'kbm' | 'pad' = 'kbm';
  private keyHandler = (e: KeyboardEvent) => this.onKey(e);
  private wheelHandler = (e: WheelEvent) => { if (Math.abs(e.deltaY) > 4) { e.preventDefault(); this.browse(0, e.deltaY > 0 ? 1 : -1); } };

  constructor() {
    if (!document.getElementById('rk-css')) {
      const st = document.createElement('style');
      st.id = 'rk-css'; st.textContent = CSS;
      document.head.appendChild(st);
    }
    this.root.className = 'rk vz';
    this.panel.className = 'rk-panel';
    this.ladder.className = 'rk-ladder';
    this.card.className = 'rk-card';
    this.hints.className = 'rk-hints';
    this.tag.className = 'rk-tag';
    const title = document.createElement('div');
    title.className = 'rk-title';
    title.innerHTML = '<span>Gun rack</span><span>Your kit</span>';
    this.panel.append(title, this.ladder, this.card, this.hints);
    this.panel.addEventListener('wheel', this.wheelHandler, { passive: false });
    for (const k of ['a', 'b', 'c', 'd']) { const e = document.createElement('div'); e.className = 'rk-br ' + k; this.br.push(e); }
    this.root.append(this.panel, ...this.br, this.tag);
    document.body.appendChild(this.root);
  }

  get open() { return !!this.hooks; }
  /** the right edge of the readout column (css px): the camera frames the rack to the right of it */
  get columnRight() { return this.hooks ? this.panel.getBoundingClientRect().right : 0; }
  /** what the readout is on right now */
  get browsing(): { kind: RackKind; id: number } { const r = this.rows[this.row]; return { kind: r.kind, id: r.ids[this.cur[this.row]] }; }

  show(cls: ClassId, lo: Loadout, hooks: RackHooks) {
    this.cls = cls; this.lo = { ...lo }; this.hooks = hooks; this.changed = false;
    this.row = 0;
    this.syncCursors();
    this.pad.swallow();
    window.addEventListener('keydown', this.keyHandler, true);
    this.root.classList.add('on');
    this.paint();
    this.hooks.browse(this.browsing.kind, this.browsing.id);
  }

  close() {
    if (!this.hooks) return;
    const h = this.hooks;
    this.hooks = null;
    window.removeEventListener('keydown', this.keyHandler, true);
    this.root.classList.remove('on');
    this.bracket(null);
    h.close(this.changed);
  }

  dispose() { window.removeEventListener('keydown', this.keyHandler, true); this.root.remove(); }

  /** the browsed item's screen rect (css px), or null when it's off the rack / off screen */
  bracket(r: { x0: number; y0: number; x1: number; y1: number } | null) {
    const on = !!r;
    for (const e of this.br) e.style.opacity = on ? '1' : '0';
    this.tag.style.opacity = on ? '1' : '0';
    if (!r) return;
    const pad = 8, s = 16;
    const x0 = r.x0 - pad, y0 = r.y0 - pad, x1 = r.x1 + pad - s, y1 = r.y1 + pad - s;
    const put = (e: HTMLElement, x: number, y: number) => { e.style.left = `${x}px`; e.style.top = `${y}px`; };
    put(this.br[0], x0, y0); put(this.br[1], x1, y0); put(this.br[2], x0, y1); put(this.br[3], x1, y1);
    // the name goes under the item, or over it when that would leave the screen
    const below = y1 + s + 6, h = window.innerHeight;
    put(this.tag, x0, below + 16 < h ? below : y0 - 20);
  }

  private syncCursors() {
    const ids = [this.cls, this.lo.secondary, this.lo.primary, this.lo.grenade];
    this.rows.forEach((r, i) => { this.cur[i] = Math.max(0, r.ids.indexOf(ids[i])); });
  }

  private equippedId(row: number) { return row === 0 ? this.cls : row === 1 ? this.lo.secondary : row === 2 ? this.lo.primary : this.lo.grenade; }

  private equip() {
    if (!this.hooks) return;
    const r = this.rows[this.row], id = r.ids[this.cur[this.row]];
    if (this.equippedId(this.row) === id) return;
    this.changed = true;
    this.hooks.sound('ui_select');
    if (this.row === 0) {
      this.cls = id as ClassId;
      this.hooks.equipClass(this.cls);
      return; // the director hands the new class's kit back through setKit
    }
    if (this.row === 1) this.lo.secondary = id; else if (this.row === 2) this.lo.primary = id; else this.lo.grenade = id;
    this.hooks.equipLoadout({ ...this.lo });
    this.paint();
  }

  /** the class changed: its own saved kit comes up on the rows */
  setKit(cls: ClassId, lo: Loadout) {
    this.cls = cls; this.lo = { ...lo };
    const keep = this.cur[this.row];
    this.syncCursors();
    this.cur[this.row] = keep;
    this.paint();
  }

  /** rows stop at the top and bottom of the ladder; items wrap round */
  private browse(dRow: number, dItem: number) {
    const was = this.row, wasItem = this.cur[this.row];
    if (dRow) this.row = Math.max(0, Math.min(this.rows.length - 1, this.row + dRow));
    if (dItem) { const n = this.rows[this.row].ids.length; this.cur[this.row] = (this.cur[this.row] + dItem + n) % n; }
    if ((dRow || dItem) && was === this.row && wasItem === this.cur[this.row]) return; // against the end of the ladder
    this.hooks?.sound('ui_move');
    this.paint();
    const b = this.browsing;
    this.hooks?.browse(b.kind, b.id);
  }

  private onKey(e: KeyboardEvent) {
    if (!this.hooks) return;
    const k = e.code;
    let used = true;
    if (k === 'ArrowUp' || k === 'KeyW') this.browse(-1, 0);
    else if (k === 'ArrowDown' || k === 'KeyS') this.browse(1, 0);
    else if (k === 'ArrowLeft' || k === 'KeyA') this.browse(0, -1);
    else if (k === 'ArrowRight' || k === 'KeyD') this.browse(0, 1);
    else if (k === 'Enter' || k === 'Space' || k === 'KeyE') this.equip();
    else if (k === 'Escape' || k === 'Backspace') this.close();
    else used = false;
    if (used) { e.preventDefault(); e.stopImmediatePropagation(); }
  }

  update() {
    if (!this.hooks) return;
    const p = this.pad.poll();
    if (p & 1) this.equip();
    else if (p & 2) this.close();
    else if (p & 4) this.browse(-1, 0);
    else if (p & 8) this.browse(1, 0);
    else if (p & 16) this.browse(0, -1);
    else if (p & 32) this.browse(0, 1);
  }

  private glyph(g: 'cross' | 'circle', key: string) { return this.glyphs === 'pad' ? psGlyph(g) : `<span class="cap-k">${key}</span>`; }

  /** {tri} {r3} ... in the class copy → PS5 glyphs on the pad, keys on mouse and keyboard */
  private buttons(s: string) {
    const pad = this.glyphs === 'pad';
    const chip = (t: string) => `<span class="rk-btn">${t}</span>`;
    // a pad button named in brackets in the weapon copy, "Aim (L2)", becomes a chip like the rest
    const named = pad ? esc(s).replace(/\((L1|L2|L3|R1|R2|R3)\)/g, (_, b: string) => chip(b)) : esc(s);
    return named.replace(/\{(\w+)\}/g, (_, b: string) => {
      switch (b) {
        case 'tri': return pad ? psGlyph('triangle') : chip('Q');
        case 'circle': return pad ? psGlyph('circle') : chip('C');
        case 'x': return pad ? psGlyph('cross') : chip('Space');
        case 'r3': return chip(pad ? 'R3' : 'F');
        case 'r2': return chip(pad ? 'R2' : 'Click');
        case 'l1': return chip(pad ? 'L1' : 'Shift');
        case 'r1': return chip(pad ? 'R1' : 'G');
        case 'dd': return pad ? dpad('d') : chip('T');
        default: return '';
      }
    });
  }

  // ---------------------------------------------------------------- paint
  private itemLabel(kind: RackKind, id: number, size = 18) {
    return kind === 'cls' ? emblemSvg(id, size) + `<span class="nm">${esc(CLASSES[id].name)}</span>`
      : kind === 'nade' ? `<span class="dia" style="background:${NADE_HEX[id] ?? '#ccc'}"></span><span class="nm">${esc(GRENADES[id].name.replace(/ Grenade$/, ''))}</span>`
      : `<span class="nm">${esc(WEAPONS[id].name)}</span>`;
  }

  private paint() {
    // the ladder
    this.ladder.innerHTML = '';
    this.rows.forEach((r, ri) => {
      const on = ri === this.row;
      const step = document.createElement('div');
      step.className = 'rk-step' + (on ? ' on' : '');
      const id = on ? r.ids[this.cur[ri]] : this.equippedId(ri);
      const val = document.createElement('div');
      val.className = 'val';
      if (on) {
        const eq = id === this.equippedId(ri);
        val.innerHTML = `<span class="arr" data-d="-1">◀</span><span class="grow">${this.itemLabel(r.kind, id)}</span>${eq ? '<span class="eqd">EQUIPPED</span>' : `<span class="eqd off">${this.glyph('cross', 'E')}EQUIP</span>`}<span class="arr" data-d="1">▶</span>`;
        val.querySelectorAll<HTMLElement>('.arr').forEach((a) => { a.onclick = (e) => { e.stopPropagation(); this.browse(0, Number(a.dataset.d)); }; });
        (val.querySelector('.grow') as HTMLElement).onclick = (e) => { e.stopPropagation(); this.equip(); };
      } else val.innerHTML = this.itemLabel(r.kind, id);
      const lab = document.createElement('div');
      lab.className = 'lab'; lab.textContent = r.label;
      step.append(lab, val);
      if (on) {
        const pips = document.createElement('div');
        pips.className = 'rk-pips';
        pips.innerHTML = r.ids.map((pid, ii) => `<i class="${ii === this.cur[ri] ? 'cur' : pid === this.equippedId(ri) ? 'eq' : ''}"></i>`).join('');
        step.appendChild(pips);
      }
      step.onclick = () => { if (this.row !== ri) this.browse(ri - this.row, 0); };
      this.ladder.appendChild(step);
    });
    // the card
    const r = this.rows[this.row], id = r.ids[this.cur[this.row]];
    const eq = id === this.equippedId(this.row);
    this.card.innerHTML = r.kind === 'cls' ? this.classCard(id) : r.kind === 'nade' ? this.nadeCard(GRENADES[id]) : this.gunCard(WEAPONS[id]);
    this.tag.textContent = r.kind === 'cls' ? CLASSES[id].name : r.kind === 'nade' ? GRENADES[id].name : WEAPONS[id].name;
    // ✕ lights up when the browsed item isn't the one you're wearing (the ladder says EQUIPPED when it is)
    const eqHint = `<span class="eqh${eq ? '' : ' hot'}">${this.glyph('cross', 'E')}Equip</span>`;
    this.hints.innerHTML = this.glyphs === 'pad'
      ? `<span><span class="ar">▲▼</span>Row</span><span><span class="ar">◀▶</span>Browse</span>${eqHint}<span>${psGlyph('circle')}Back to the bar</span>`
      : `<span><span class="cap-k">W S</span>Row</span><span><span class="cap-k">A D</span>Browse</span>${eqHint}<span><span class="cap-k">ESC</span>Back</span>`;
    (this.hints.querySelector('.eqh') as HTMLElement).onclick = () => this.equip();
  }

  private stat(label: string, frac: number, value: string) {
    return `<span class="l">${label}</span><i><b style="width:${Math.round(Math.max(0.04, Math.min(1, frac)) * 100)}%"></b></i><span class="v">${value}</span>`;
  }

  private gunCard(w: WeaponDef) {
    const per = w.damage * w.pellets;
    const sps = shotsPerSec(w);
    const rpm = Math.round(sps * 60);
    const reach = Math.min(w.falloffEnd, w.range);
    const reload = w.reloadTicks * tick;
    const chips = [`<span class="rk-chip">${w.role}</span>`, `<span class="rk-chip on">${AP_NAME[w.ap] ?? 'AP'}</span>`, `<span class="rk-chip">${FIRE_NAME[w.fire] ?? ''}</span>`];
    if (w.akimbo) chips.push('<span class="rk-chip">AKIMBO</span>');
    return `<div class="rk-name">${esc(w.name)}</div>${w.sub ? `<div class="rk-sub">${esc(w.sub)}</div>` : ''}
      <div class="rk-chips">${chips.join('')}</div>
      <div class="rk-blurb">${this.buttons(w.blurb)}</div>
      <div class="rk-stats">
        ${this.stat('DAMAGE', per / 170, w.pellets > 1 ? `${w.damage} × ${w.pellets}` : String(w.damage))}
        ${this.stat('RATE', rpm / 800, `${rpm} RPM`)}
        ${this.stat('DPS', (per * sps) / 180, String(Math.round(per * sps)))}
        ${this.stat('MAGAZINE', w.mag / 75, String(w.mag))}
        ${this.stat('RANGE', reach / 180, `${Math.round(reach)} M`)}
        ${this.stat('RELOAD', 1 - Math.min(1, reload / 3), w.rounds ? `${reload.toFixed(2)} S/RD` : `${reload.toFixed(1)} S`)}
      </div>`;
  }

  private nadeCard(g: GrenadeDef) {
    const fuse = g.fuse > 0 ? `${g.fuse} S FUSE` : 'IMPACT';
    const what = g.stun ? `${g.stun} S STUN` : g.gasSecs ? `${g.gasSecs} S CLOUD` : g.slow ? `${g.slow} S SLOW` : g.sticky ? 'STICKY' : '';
    const how = g.fuse > 0 ? `Hold {r1} to cook it, let go to throw. Hold it past ${g.fuse} s and it goes off in your hand.` : 'Tap {r1}: it goes off on whatever it hits.';
    return `<div class="rk-name">${esc(g.name)}</div>
      <div class="rk-chips"><span class="rk-chip on">${fuse}</span>${what ? `<span class="rk-chip">${what}</span>` : ''}<span class="rk-chip">${AP_NAME[g.ap] ?? 'AP'}</span></div>
      <div class="rk-blurb">${esc(g.blurb)}</div>
      <div class="rk-watch">${this.buttons(how)}</div>
      <div class="rk-stats">
        ${this.stat('DAMAGE', g.damage / 90, g.damage ? String(g.damage) : '—')}
        ${this.stat('RADIUS', g.radius / 9, `${g.radius} M`)}
        ${this.stat('FUSE', g.fuse > 0 ? 1 - g.fuse / 4 : 1, g.fuse > 0 ? `${g.fuse} S` : 'ON HIT')}
      </div>`;
  }

  private classCard(i: number) {
    const c = CLASSES[i], info = CLASS_INFO[i];
    const dance = info ? info.lines.map((l) => `<div><span class="k">${esc(l.k)}</span>${this.buttons(l.text)}</div>`).join('') : '';
    return `<div class="rk-name">${emblemSvg(i, 40)}${esc(c.name)}<span class="rk-role">${esc(c.role)}</span></div>
      ${info ? `<div class="rk-blurb rk-bio">${esc(info.bio)}</div>` : ''}
      <div class="rk-loop">Every class: prime a robot, then detonate it. The robot's shield must be down first.</div>
      <div class="rk-dance">${dance}</div>
      ${info ? `<div class="rk-watch">${this.buttons(info.watch)}</div>` : ''}
      <div class="rk-stats two">
        ${this.stat('SHIELD', c.shieldMax / 150, String(c.shieldMax))}
        ${this.stat('JET HEAT', c.heatMax / 145, String(c.heatMax))}
        ${this.stat('COOL /S', c.heatCool / 18, String(c.heatCool))}
        ${this.stat('TURNING', c.turnRate / 3.4, c.turnRate.toFixed(1))}
      </div>`;
  }
}
